import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface AppBanner {
  /** Unique identifier — used to add/remove. */
  id: string;
  /** Lower number = shown first. Default 0. */
  priority?: number;
  /** The banner content (rendered inside BannerStack). */
  content: ReactNode;
}

interface BannerContextValue {
  banners: AppBanner[];
  addBanner: (banner: AppBanner) => void;
  removeBanner: (id: string) => void;
}

const BannerContext = createContext<BannerContextValue | null>(null);

export function BannerProvider({ children }: { children: ReactNode }) {
  const [banners, setBanners] = useState<AppBanner[]>([]);

  const addBanner = useCallback((banner: AppBanner) => {
    setBanners(prev => {
      // Replace if same id already exists
      const filtered = prev.filter(b => b.id !== banner.id);
      return [...filtered, banner];
    });
  }, []);

  const removeBanner = useCallback((id: string) => {
    setBanners(prev => prev.filter(b => b.id !== id));
  }, []);

  const sorted = useMemo(
    () => [...banners].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
    [banners],
  );

  return (
    <BannerContext.Provider value={{ banners: sorted, addBanner, removeBanner }}>
      {children}
    </BannerContext.Provider>
  );
}

export function useBanners() {
  const ctx = useContext(BannerContext);
  if (!ctx) throw new Error('useBanners must be used within BannerProvider');
  return ctx;
}
